"use client";

import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useRef } from "react";

import { usePathname } from "next/navigation";

/**
 * Length of the arbitration window. The first `requestDisplay` opens a short
 * window during which competing triggers can join; once it closes, the lowest
 * `priority` number wins and locks the slot for the rest of the page lifetime.
 */
export const ONSITE_PERSONALIZATION_ARBITRATION_WINDOW_MS = 300;

const GLOBAL_PAGE_LOCKS_KEY = "__onsitePersonalizationPageLocks";

type GlobalWithPageLocks = typeof globalThis & {
  [GLOBAL_PAGE_LOCKS_KEY]?: Set<string>;
};

const getPageLocks = (): Set<string> => {
  const globalObject = globalThis as GlobalWithPageLocks;

  if (!globalObject[GLOBAL_PAGE_LOCKS_KEY]) {
    globalObject[GLOBAL_PAGE_LOCKS_KEY] = new Set<string>();
  }

  return globalObject[GLOBAL_PAGE_LOCKS_KEY];
};

const isSessionLocked = (pathname: string | null): boolean => {
  if (!pathname) return false;
  return getPageLocks().has(pathname);
};

const setSessionLock = (pathname: string | null): void => {
  if (!pathname) return;
  getPageLocks().add(pathname);
};

const clearSessionLock = (pathname: string | null): void => {
  if (!pathname) return;
  getPageLocks().delete(pathname);
};

type OnsitePersonalizationPriorityContextValue = {
  requestDisplay: (priority: number) => Promise<boolean>;
};

const OnsitePersonalizationPriorityContext = createContext<OnsitePersonalizationPriorityContextValue | null>(null);

type OnsitePersonalizationModalPriorityProviderProps = {
  children: ReactNode;
};

type Candidate = {
  priority: number;
  resolve: (won: boolean) => void;
};

export function OnsitePersonalizationModalPriorityProvider({
  children,
}: OnsitePersonalizationModalPriorityProviderProps) {
  const pathname = usePathname();
  const openedPriorityRef = useRef<number | null>(null);
  const candidatesRef = useRef<Candidate[]>([]);
  const arbitrationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const isMountedRef = useRef(false);
  const previousPathnameRef = useRef(pathname);

  const settleArbitration = useCallback(() => {
    arbitrationTimerRef.current = null;
    const candidates = candidatesRef.current;
    candidatesRef.current = [];

    if (candidates.length === 0) {
      return;
    }

    const winner = candidates.reduce((best, c) => (c.priority < best.priority ? c : best));
    openedPriorityRef.current = winner.priority;
    setSessionLock(pathnameRef.current);
    candidates.forEach((c) => c.resolve(c === winner));
  }, []);

  useEffect(
    () => () => {
      if (arbitrationTimerRef.current !== null) {
        clearTimeout(arbitrationTimerRef.current);
        arbitrationTimerRef.current = null;
      }
      candidatesRef.current.forEach((c) => c.resolve(false));
      candidatesRef.current = [];
    },
    []
  );

  useEffect(() => {
    // Skip on initial mount — reset is only meaningful on navigation.
    // This preserves the sessionStorage lock when the provider remounts on the
    // same page (e.g. server-component re-render) without a true navigation.
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }

    // Reset the slot lock on navigation so each page gets its own arbitration
    clearSessionLock(previousPathnameRef.current);
    openedPriorityRef.current = null;
    previousPathnameRef.current = pathname;
  }, [pathname]);

  const requestDisplay = useCallback(
    (priority: number): Promise<boolean> => {
      if (openedPriorityRef.current !== null || isSessionLocked(pathnameRef.current)) {
        return Promise.resolve(false);
      }

      return new Promise<boolean>((resolve) => {
        candidatesRef.current.push({ priority, resolve });
        if (arbitrationTimerRef.current === null) {
          arbitrationTimerRef.current = setTimeout(settleArbitration, ONSITE_PERSONALIZATION_ARBITRATION_WINDOW_MS);
        }
      });
    },
    [settleArbitration]
  );

  return (
    <OnsitePersonalizationPriorityContext.Provider value={{ requestDisplay }}>
      {children}
    </OnsitePersonalizationPriorityContext.Provider>
  );
}

export function useOnsitePersonalizationPriority(): OnsitePersonalizationPriorityContextValue {
  const context = useContext(OnsitePersonalizationPriorityContext);

  if (!context) {
    return { requestDisplay: () => Promise.resolve(true) };
  }

  return context;
}
